/*
 * snd-wishlist-drawer.js — SND-17537
 * -------------------------------------------------------------------------
 * Fills snippets/snd-wishlist-drawer.liquid from Wishlist King's headless state
 * and keeps the header heart bubble (snippets/header-actions.liquid) in sync.
 *
 *   WishlistKing._state.observeWishlist({ wishlistId: 'mine' }).subscribe(list)
 *     list.items[] = { id, variantId, product: { handle, title, vendor, url,
 *                      featured_image, price_min, compare_at_price_min,
 *                      available, variants[] } }   (products.js shape)
 *   WishlistKing.removeWishlistItem({ wishlistItemId })
 *
 * SND-20226 (W2): several lists (assets/snd-wishlists.js). The chip row switches
 * the drawer between them; the main list keeps the Wishlist King rows above,
 * any other list builds the same rows from /products/<handle>.js. The header
 * bubble = unique products across all lists.
 *
 * Add to cart = the theme's own flow (identical to snd-search-panel.js): POST
 * /cart/add.js + CartLinesUpdateEvent, so the cart bubble, cart drawer auto-open
 * and section re-render behave like any other add-to-cart on the site.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

const waitFor = (get, tries = 80) =>
  new Promise((resolve) => {
    const tick = () => {
      let v = null;
      try { v = get(); } catch (e) { v = null; }
      if (v) return resolve(v);
      if (--tries <= 0) return resolve(null);
      setTimeout(tick, 250);
    };
    tick();
  });

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const imgUrl = (src, w) => {
  if (!src) return '';
  try {
    const u = new URL(src, location.origin);
    u.searchParams.set('width', String(w));
    return u.toString();
  } catch (e) {
    return src;
  }
};

const lists = () => window.SndWishlists || null;
const MAIN = 'mine';

class SndWishlistDrawer extends HTMLElement {
  #items = [];
  #mainItems = [];
  #ready = false;
  #unsub = null;
  #list = MAIN;
  #unique = 0;
  #token = 0;

  connectedCallback() {
    this.list = this.querySelector('[data-snd-wd-list]');
    this.empty = this.querySelector('[data-snd-wd-empty]');
    this.loading = this.querySelector('[data-snd-wd-loading]');
    this.footer = this.querySelector('[data-snd-wd-footer]');
    this.badge = this.querySelector('[data-snd-wd-badge]');
    this.live = this.querySelector('[data-snd-wd-live]');
    this.drawer = this.closest('theme-drawer');
    this.triggers = () => document.querySelectorAll('[data-snd-wish-trigger]');
    this.listBar = this.querySelector('[data-snd-wd-lists]');
    this.chips = this.querySelector('[data-snd-wd-chips]');

    this.addEventListener('click', this.#onClick);
    document.addEventListener('snd:wishlists:change', this.#onLists);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerState);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerState);

    waitFor(() => {
      const wk = window.WishlistKing;
      return wk && wk._state && typeof wk._state.observeWishlist === 'function' ? wk : null;
    }).then((wk) => {
      if (!wk) {
        // App not loaded: header button is already hidden by snd-card-actions.js; show the empty state anyway.
        this.#items = [];
        this.#ready = true;
        this.#render();
        return;
      }
      try {
        const sub = wk._state.observeWishlist({ wishlistId: MAIN }).subscribe((list) => {
          this.#mainItems = ((list && list.items) || []).filter((i) => i && i.product && !i.product.hidden);
          this.#ready = true;
          this.#refresh();
        });
        this.#unsub = sub && typeof sub.unsubscribe === 'function' ? () => sub.unsubscribe() : null;
      } catch (error) {
        console.warn('[snd-wishlist-drawer] observe', error);
        this.#ready = true;
        this.#render();
      }
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerState);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerState);
    document.removeEventListener('snd:wishlists:change', this.#onLists);
    if (this.#unsub) this.#unsub();
  }

  #onLists = () => {
    if (this.#ready) this.#refresh();
  };

  /* Main list -> Wishlist King rows; any other list -> rows built from products.js. */
  async #refresh() {
    const store = lists();
    const all = store ? store.lists() : [{ id: MAIN, main: true, name: '', itemHandles: this.#mainItems.map((i) => i.product.handle) }];
    if (!all.some((l) => l.id === this.#list)) this.#list = MAIN;
    this.#renderChips(all);

    const unique = store ? store.uniqueCount() : this.#mainItems.length;
    this.#syncTriggers(unique, unique > this.#unique);
    this.#unique = unique;

    const token = ++this.#token;
    if (this.#list === MAIN || !store) {
      this.#items = this.#mainItems;
    } else {
      const list = all.find((l) => l.id === this.#list);
      const products = await Promise.all(list.itemHandles.map((h) => store.product(h)));
      if (token !== this.#token) return; // a newer refresh won
      this.#items = products
        .filter((p) => p && !p.hidden)
        .map((p) => ({ id: p.handle, variantId: null, list: list.id, product: { ...p, url: this.#productUrl(p.handle) } }));
    }
    this.#render();
  }

  #productUrl(handle) {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return `${root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}`;
  }

  #renderChips(all) {
    if (!this.listBar || !this.chips) return;
    const show = all.length > 1;
    this.listBar.hidden = !show;
    if (!show) {
      this.chips.innerHTML = '';
      delete this.chips.dataset.ids;
      return;
    }
    // Update in place when the set of lists is unchanged: replacing the button that is
    // being clicked detaches the event target, and theme-drawer reads that as a click outside.
    const ids = all.map((l) => l.id).join('|');
    if (this.chips.dataset.ids !== ids) {
      this.chips.dataset.ids = ids;
      this.chips.innerHTML = all
        .map((l) => `<button type="button" class="snd-chip snd-chip--point snd-chip--sm snd-wd__chip" data-snd-wd-chip="${esc(l.id)}" aria-pressed="false"><span class="snd-wd__chip-t"></span><span class="snd-wd__chip-n"></span></button>`)
        .join('');
    }
    all.forEach((l) => {
      const b = this.chips.querySelector(`[data-snd-wd-chip="${CSS.escape(l.id)}"]`);
      if (!b) return;
      b.setAttribute('aria-pressed', l.id === this.#list ? 'true' : 'false');
      b.querySelector('.snd-wd__chip-t').textContent = l.name;
      b.querySelector('.snd-wd__chip-n').textContent = String(l.itemHandles.length);
    });
  }

  #onDrawerState = (event) => {
    const target = event.target;
    if (!target || target.id !== 'snd-wishlist-drawer') return;
    const open = target.hasAttribute('open');
    this.triggers().forEach((t) => t.setAttribute('aria-expanded', open ? 'true' : 'false'));
  };

  #syncTriggers(count, pop) {
    this.triggers().forEach((t) => {
      const bubble = t.querySelector('[data-snd-wish-count]');
      if (bubble) {
        bubble.textContent = count > 99 ? '99+' : String(count);
        bubble.hidden = count === 0;
        if (pop && count > 0) {
          bubble.classList.remove('is-pop');
          // restart the animation
          void bubble.offsetWidth;
          bubble.classList.add('is-pop');
        }
      }
      t.toggleAttribute('data-has-items', count > 0);
      const label = this.dataset.tCount ? this.dataset.tCount.replace('[count]', String(count)) : '';
      if (label) t.setAttribute('aria-label', label);
    });
  }

  #variantOf(item) {
    const variants = (item.product && item.product.variants) || [];
    return variants.find((v) => Number(v.id) === Number(item.variantId)) || variants[0] || null;
  }

  #money(cents) {
    if (cents == null || Number.isNaN(Number(cents))) return '';
    try {
      return formatMoney(Number(cents), this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'EUR');
    } catch (e) {
      return (Number(cents) / 100).toFixed(2);
    }
  }

  #row(item) {
    const p = item.product;
    const v = this.#variantOf(item);
    const url = v && v.id ? `${p.url}?variant=${v.id}` : p.url;
    const image = (v && v.featured_image && v.featured_image.src) || p.featured_image || '';
    const price = v ? v.price : p.price_min;
    const compare = v ? v.compare_at_price : p.compare_at_price_min;
    const available = v ? v.available !== false : p.available !== false;
    const variantTitle = v && v.title && v.title !== 'Default Title' ? v.title : '';
    const t = this.dataset;

    return `
      <li class="snd-wd__row" data-snd-wd-row="${esc(item.id)}" data-handle="${esc(p.handle)}" data-list-id="${esc(item.list || MAIN)}">
        <a class="snd-wd__img" href="${esc(url)}" tabindex="-1" aria-hidden="true">
          ${image ? `<img src="${esc(imgUrl(image, 240))}" alt="" loading="lazy" width="120" height="120">` : ''}
        </a>
        <div class="snd-wd__info">
          ${p.vendor ? `<span class="snd-wd__vendor">${esc(p.vendor)}</span>` : ''}
          <a class="snd-wd__title" href="${esc(url)}">${esc(p.title)}</a>
          ${variantTitle ? `<span class="snd-wd__variant">${esc(variantTitle)}</span>` : ''}
          <span class="snd-wd__price">${esc(this.#money(price))}${compare && Number(compare) > Number(price) ? `<s>${esc(this.#money(compare))}</s>` : ''}</span>
        </div>
        <div class="snd-wd__actions">
          <button type="button" class="button snd-wd__add" data-snd-wd-add="${v ? esc(v.id) : ''}" ${available && v ? '' : 'disabled'}>${esc(available && v ? t.tAdd : t.tSoldOut)}</button>
          <button type="button" class="snd-wd__remove" data-snd-wd-remove="${esc(item.id)}" aria-label="${esc(t.tRemove)}" title="${esc(t.tRemove)}">
            <svg viewBox="0 0 24 24" aria-hidden="true" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.4107 19.9677C7.58942 17.858 2 13.0348 2 8.69444C2 5.82563 4.10526 3.5 7 3.5C8.5 3.5 10 4 12 6C14 4 15.5 3.5 17 3.5C19.8947 3.5 22 5.82563 22 8.69444C22 13.0348 16.4106 17.858 13.5893 19.9677C12.6399 20.6776 11.3601 20.6776 10.4107 19.9677Z"/></svg>
          </button>
        </div>
      </li>`;
  }

  #render() {
    if (!this.list) return;
    const n = this.#items.length;
    if (this.loading) this.loading.hidden = this.#ready;
    if (this.empty) this.empty.hidden = !this.#ready || n > 0;
    if (this.footer) this.footer.hidden = !this.#ready || n === 0;
    this.list.hidden = !this.#ready || n === 0;
    if (this.badge) {
      this.badge.hidden = n === 0;
      this.badge.textContent = n > 99 ? '99+' : String(n);
    }
    if (n > 0) this.list.innerHTML = this.#items.map((i) => this.#row(i)).join('');
    else this.list.innerHTML = '';
  }

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const chip = target.closest('[data-snd-wd-chip]');
    if (chip) {
      event.preventDefault();
      this.#list = chip.getAttribute('data-snd-wd-chip') || MAIN;
      this.#refresh();
      return;
    }
    const remove = target.closest('[data-snd-wd-remove]');
    if (remove) {
      event.preventDefault();
      this.#remove(remove);
      return;
    }
    const add = target.closest('[data-snd-wd-add]');
    if (add) {
      event.preventDefault();
      this.#addToCart(add);
    }
  };

  async #remove(button) {
    const wk = window.WishlistKing;
    const id = button.getAttribute('data-snd-wd-remove');
    const row = button.closest('[data-snd-wd-row]');
    const listId = row && row.getAttribute('data-list-id');
    if (!id) return;
    if (listId && listId !== MAIN) {
      row?.classList.add('is-leaving');
      await lists()?.remove(listId, id);
      if (this.live) this.live.textContent = this.dataset.tRemove || '';
      return;
    }
    if (!wk) return;
    row?.classList.add('is-leaving');
    try {
      const handle = row && row.getAttribute('data-handle');
      if (lists() && handle) await lists().remove(MAIN, handle);
      else await wk.removeWishlistItem({ wishlistItemId: id });
      // the observer re-renders; announce for screen readers
      if (this.live) this.live.textContent = this.dataset.tRemove || '';
    } catch (error) {
      row?.classList.remove('is-leaving');
      console.warn('[snd-wishlist-drawer] remove', error);
    }
  }

  async #addToCart(button) {
    const id = Number(button.getAttribute('data-snd-wd-add'));
    if (!id || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    const addUrl = Theme.routes.cart_add_url.replace(/(\.js)?$/, '.js');
    const cartUrl = `${Theme.routes.cart_url.replace(/\.js$/, '')}.js`;
    let deferred = null;

    try {
      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(id), quantity: 1 }],
          promise: deferred.promise,
        })
      );
    } catch (error) {
      console.warn('[snd-wishlist-drawer] cart event', error);
      deferred = null;
    }

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const failed = !response.ok || Boolean(data.status);
      deferred?.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: failed, items: cart.items, sections: data.sections, source: 'snd-wishlist-drawer', itemCount: 1 },
      });
      if (failed) {
        console.warn('[snd-wishlist-drawer] add to cart', data.description || data.message);
      } else {
        button.dataset.state = 'added';
        const label = button.textContent;
        button.textContent = this.dataset.tAdded || label;
        setTimeout(() => {
          delete button.dataset.state;
          button.textContent = this.dataset.tAdd || label;
        }, 1800);
        // The cart drawer auto-opens on top (settings.auto_open_cart_drawer): step aside so two drawers do not stack.
        if (this.dataset.autoOpenCart === '1' && this.drawer && typeof this.drawer.close === 'function') {
          setTimeout(() => this.drawer.close(), 250);
        }
      }
      if (!failed && !deferred) window.location.href = Theme.routes.cart_url;
    } catch (error) {
      console.warn('[snd-wishlist-drawer] add to cart', error);
      deferred?.reject(error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('snd-wishlist-drawer')) {
  customElements.define('snd-wishlist-drawer', SndWishlistDrawer);
}
